import { DecimalPipe } from '@angular/common';
import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { CurrentContextService } from '../../core/context/current-context.service';
import { liveStatusTranslationKey } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { CatalogOffering, CatalogService, CatalogWarehouse } from './catalog.service';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import {
  InventoryAuditTrailEntry,
  InventoryCostingApiService,
  InventoryGlPostingOutboxSummary,
  InventoryReconciliationReport
} from './inventory-costing-api.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-operability',
  imports: [
    DecimalPipe, AinPageComponent, AinStateComponent, InfoHintComponent, JalaliDateInputComponent,
    FormsModule, TableModule, ButtonModule, SelectModule, InputTextModule, RouterLink, AinCanAccessDirective
  ],
  template: `
    <ain-page [title]="t('catalog.costing.operability.title')" [description]="t('catalog.costing.operability.description')">
      <ain-info-hint page-title-addon
        [titleKey]="'catalog.costing.operability.help.title'"
        [descriptionKey]="'catalog.costing.operability.help.description'"
        [bulletKeys]="[
          'catalog.costing.operability.help.bullets.summary',
          'catalog.costing.operability.help.bullets.reconciliation',
          'catalog.costing.operability.help.bullets.audit'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (!hasAccess()) {
        <ain-state type="error" [title]="t('catalog.costing.states.noPermission')" [message]="t('catalog.costing.operability.noPermissionMessage')" />
      } @else {
        <div class="toolbar">
          <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="load()"></button>
          <span class="live-status" [class]="liveUpdateService.connectionState()">
            {{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}
          </span>
        </div>

        <section class="panel">
          <h3>{{ t('catalog.costing.operability.outboxTitle') }}
            <ain-info-hint [titleKey]="'catalog.costing.operability.outboxHelp.title'" [descriptionKey]="'catalog.costing.operability.outboxHelp.description'" />
          </h3>
          @if (outboxError()) {
            <ain-state type="error" [title]="t('catalog.costing.states.loadFailed')" />
          } @else if (outboxSummary()) {
            <div class="stats">
              <article [class.warn]="outboxSummary()!.pendingCount > 0">
                <strong>{{ t('catalog.costing.operability.pending') }}</strong>
                <span>{{ outboxSummary()!.pendingCount }}</span>
              </article>
              <article [class.warn]="outboxSummary()!.failedCount > 0">
                <strong>{{ t('catalog.costing.operability.failed') }}</strong>
                <span>{{ outboxSummary()!.failedCount }}</span>
              </article>
              <article [class.warn]="outboxSummary()!.agedPendingCount > 0">
                <strong>{{ t('catalog.costing.operability.stalePending') }}</strong>
                <span>{{ outboxSummary()!.agedPendingCount }}</span>
              </article>
              <article [class.warn]="outboxSummary()!.stuckProcessingCount > 0">
                <strong>{{ t('catalog.costing.operability.stuckProcessing') }}</strong>
                <span>{{ outboxSummary()!.stuckProcessingCount }}</span>
              </article>
              <article>
                <strong>{{ t('catalog.costing.operability.processing') }}</strong>
                <span>{{ outboxSummary()!.processingCount }}</span>
              </article>
              <article>
                <strong>{{ t('catalog.costing.operability.retryScheduled') }}</strong>
                <span>{{ outboxSummary()!.retryScheduledCount }}</span>
              </article>
            </div>
            @if (outboxSummary()!.requiresAttention) {
              <p class="attention">{{ t('catalog.costing.operability.requiresAttention') }}</p>
            }
          }
        </section>

        <section class="panel">
          <h3>{{ t('catalog.costing.operability.reconciliationTitle') }}
            <ain-info-hint [titleKey]="'catalog.costing.operability.reconciliationHelp.title'" [descriptionKey]="'catalog.costing.operability.reconciliationHelp.description'" />
          </h3>
          <details class="filters" [open]="true">
            <summary>{{ t('catalog.filter.show') }}</summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('catalog.inventory.offeringId') }}</span>
                <p-select [options]="offeringOptions()" [(ngModel)]="reconciliationFilters.offeringId" optionLabel="label" optionValue="value" [showClear]="true" [filter]="true" appendTo="body" />
              </label>
              <label>
                <span>{{ t('catalog.inventory.warehouseId') }}</span>
                <p-select [options]="warehouseOptions()" [(ngModel)]="reconciliationFilters.warehouseId" optionLabel="label" optionValue="value" [showClear]="true" appendTo="body" />
              </label>
            </div>
          </details>
          <div class="actions-row">
            <button pButton type="button" [label]="t('catalog.costing.operability.runReconciliation')" [disabled]="reconciling()" (click)="runReconciliation()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.action.export')" (click)="exportReconciliation()"></button>
            @if (reconciliation()) {
              <span [class.balanced]="reconciliation()!.isBalanced" [class.unbalanced]="!reconciliation()!.isBalanced">
                {{ reconciliation()!.isBalanced ? t('catalog.costing.operability.balanced') : t('catalog.costing.operability.unbalanced') }}
              </span>
            }
          </div>

          @if (reconciliation()) {
            @if (reconciliation()!.stockQuantityDrifts.length === 0 && reconciliation()!.costDrifts.length === 0 && reconciliation()!.outboxPostingDrifts.length === 0) {
              <p class="empty-note">{{ t('catalog.costing.operability.noMismatches') }}</p>
            } @else {
              @if (reconciliation()!.stockQuantityDrifts.length > 0) {
                <h4>{{ t('catalog.costing.operability.stockDrifts') }}</h4>
                <p-table [value]="reconciliation()!.stockQuantityDrifts">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('catalog.inventory.offeringId') }}</th>
                      <th>{{ t('catalog.inventory.warehouseId') }}</th>
                      <th>{{ t('catalog.costing.operability.balanceQty') }}</th>
                      <th>{{ t('catalog.costing.operability.ledgerQty') }}</th>
                      <th>{{ t('catalog.costing.operability.delta') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>{{ resolveOffering(row.offeringId) }}</td>
                      <td>{{ resolveWarehouse(row.warehouseId) }}</td>
                      <td>{{ row.balanceQuantity | number:'1.0-4' }}</td>
                      <td>{{ row.ledgerQuantity | number:'1.0-4' }}</td>
                      <td class="warn-cell">{{ row.delta | number:'1.0-4' }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              }

              @if (reconciliation()!.costDrifts.length > 0) {
                <h4>{{ t('catalog.costing.operability.costDrifts') }}</h4>
                <p-table [value]="reconciliation()!.costDrifts">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('catalog.inventory.offeringId') }}</th>
                      <th>{{ t('catalog.inventory.warehouseId') }}</th>
                      <th>{{ t('catalog.costing.operability.qtyDelta') }}</th>
                      <th>{{ t('catalog.costing.operability.costDelta') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>{{ resolveOffering(row.offeringId) }}</td>
                      <td>{{ resolveWarehouse(row.warehouseId) }}</td>
                      <td class="warn-cell">{{ row.quantityDelta | number:'1.0-4' }}</td>
                      <td class="warn-cell">{{ row.totalCostDelta | number:'1.2-2' }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              }

              @if (reconciliation()!.fxDrifts.length > 0) {
                <h4>{{ t('catalog.costing.operability.fxDrifts') }}</h4>
                <p-table [value]="reconciliation()!.fxDrifts">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('catalog.inventory.offeringId') }}</th>
                      <th>{{ t('catalog.inventory.warehouseId') }}</th>
                      <th>{{ t('catalog.costing.currency') }}</th>
                      <th>{{ t('catalog.costing.operability.balanceCost') }}</th>
                      <th>{{ t('catalog.costing.operability.ledgerCost') }}</th>
                      <th>{{ t('catalog.costing.operability.delta') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>{{ resolveOffering(row.offeringId) }}</td>
                      <td>{{ resolveWarehouse(row.warehouseId) }}</td>
                      <td>{{ row.currencyCode }}</td>
                      <td>{{ row.balanceTotalCost | number:'1.2-2' }}</td>
                      <td>{{ row.ledgerTotalCost | number:'1.2-2' }}</td>
                      <td class="warn-cell">{{ row.delta | number:'1.2-2' }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              }

              @if (reconciliation()!.outboxPostingDrifts.length > 0) {
                <h4>{{ t('catalog.costing.operability.postingDrifts') }}</h4>
                <p-table [value]="reconciliation()!.outboxPostingDrifts">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('catalog.costing.operability.eventType') }}</th>
                      <th>{{ t('catalog.costing.operability.outboxStatus') }}</th>
                      <th>{{ t('catalog.costing.operability.postingStatus') }}</th>
                      <th>{{ t('catalog.costing.operability.driftReason') }}</th>
                      <th></th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>{{ row.eventType }}</td>
                      <td>{{ row.outboxStatus }}</td>
                      <td>{{ row.postingStatus || '-' }}</td>
                      <td>{{ driftReasonLabel(row.driftReason) }}</td>
                      <td>
                        @if (row.movementId) {
                          <a [routerLink]="['/inventory/movements', row.movementId]">{{ t('catalog.costing.operability.openMovement') }}</a>
                        }
                      </td>
                    </tr>
                  </ng-template>
                </p-table>
              }
            }
          }
        </section>

        <section class="panel">
          <h3>{{ t('catalog.costing.operability.auditTitle') }}
            <ain-info-hint [titleKey]="'catalog.costing.operability.auditHelp.title'" [descriptionKey]="'catalog.costing.operability.auditHelp.description'" />
          </h3>
          <details class="filters" [open]="true">
            <summary>{{ t('catalog.filter.show') }}</summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('catalog.costing.operability.fromDate') }}</span>
                <ain-date-input [(ngModel)]="auditFilters.fromDate" />
              </label>
              <label>
                <span>{{ t('catalog.costing.operability.toDate') }}</span>
                <ain-date-input [(ngModel)]="auditFilters.toDate" />
              </label>
              <label>
                <span>{{ t('catalog.costing.operability.category') }}</span>
                <p-select [options]="categoryOptions()" [(ngModel)]="auditFilters.category" optionLabel="label" optionValue="value" [showClear]="true" appendTo="body" />
              </label>
            </div>
            <div class="actions-row">
              <button pButton type="button" [label]="t('catalog.action.search')" (click)="searchAuditTrail()"></button>
            </div>
          </details>

          @if (auditError()) {
            <ain-state type="error" [title]="t('catalog.costing.states.loadFailed')" />
          } @else {
            <p-table
              [value]="auditItems()"
              [lazy]="true"
              [paginator]="true"
              [rows]="auditPageSize"
              [first]="auditFirst"
              [totalRecords]="auditTotalCount()"
              (onLazyLoad)="onAuditPage($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('catalog.costing.operability.auditCategory') }}</th>
                  <th>{{ t('catalog.inventory.reference') }}</th>
                  <th>{{ t('catalog.filter.status') }}</th>
                  <th>{{ t('catalog.costing.gl.status') }}</th>
                  <th>{{ t('catalog.costing.operability.occurredAt') }}</th>
                  <th>{{ t('catalog.costing.operability.amount') }}</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ categoryLabel(row.category) }}</td>
                  <td>{{ row.referenceNumber || '-' }}</td>
                  <td>{{ row.operationalStatus }}</td>
                  <td>{{ row.postingStatus || row.postingEvidenceStatus || '-' }}</td>
                  <td>{{ row.occurredAtUtc }}</td>
                  <td>
                    @if (row.amount != null) {
                      {{ row.amount | number:'1.2-2' }} {{ row.currencyCode || '' }}
                    } @else {
                      -
                    }
                  </td>
                  <td class="link-cell">
                    @if (row.movementId) {
                      <a [routerLink]="['/inventory/movements', row.movementId]">{{ t('catalog.costing.operability.openMovement') }}</a>
                    }
                    @if (row.journalEntryId || row.postingEvidenceJournalEntryId) {
                      <a [routerLink]="['/financial/management/journal-entry', row.postingEvidenceJournalEntryId || row.journalEntryId, 'edit']">{{ t('catalog.costing.gl.openJournal') }}</a>
                    }
                    @if (row.category === 'revaluation') {
                      <a [routerLink]="['/inventory/costing/revaluations', row.entityId]">{{ t('catalog.costing.operability.openRevaluation') }}</a>
                    }
                    @if (canRetryRow(row)) {
                      <button pButton type="button" size="small" severity="secondary" ainCanAccess [ainCanAccessPermission]="'inventory.movements.post-gl'" [label]="t('catalog.costing.gl.retry')" [disabled]="retryingMovementId() === row.movementId" (click)="retryGl(row.movementId!)"></button>
                    }
                    <button type="button" class="tech-link" (click)="expandedEntry.set(row)">{{ t('catalog.costing.operability.showTechnical') }}</button>
                  </td>
                </tr>
              </ng-template>
              <ng-template pTemplate="emptymessage">
                <tr><td colspan="7">{{ t('catalog.costing.operability.noAuditEntries') }}</td></tr>
              </ng-template>
            </p-table>

            @if (expandedEntry()) {
              <div class="technical-panel">
                <h4>{{ t('catalog.costing.operability.technicalReference') }}</h4>
                <div class="tech-grid">
                  <div>
                    <strong>{{ t('catalog.costing.operability.entityId') }}</strong>
                    <button type="button" class="copy-btn" (click)="copyText(expandedEntry()!.entityId)">{{ expandedEntry()!.entityId }}</button>
                  </div>
                  @if (expandedEntry()!.movementId) {
                    <div>
                      <strong>{{ t('catalog.costing.operability.movementId') }}</strong>
                      <button type="button" class="copy-btn" (click)="copyText(expandedEntry()!.movementId!)">{{ expandedEntry()!.movementId }}</button>
                    </div>
                  }
                  @if (expandedEntry()!.idempotencyKey) {
                    <div>
                      <strong>{{ t('catalog.movements.idempotencyKey') }}</strong>
                      <button type="button" class="copy-btn" (click)="copyText(expandedEntry()!.idempotencyKey!)">{{ expandedEntry()!.idempotencyKey }}</button>
                    </div>
                  }
                </div>
                <button pButton type="button" severity="secondary" size="small" [label]="t('catalog.costing.operability.hideTechnical')" (click)="expandedEntry.set(null)"></button>
              </div>
            }
          }
        </section>

        @if (error()) { <p class="error">{{ error() }}</p> }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
    .live-status { font-size: 0.85rem; font-weight: 600; padding: 4px 10px; border-radius: 999px; background: #f3f4f6; color: #6b7280; }
    .live-status.connected { background: #dcfce7; color: #166534; }
    .live-status.connecting, .live-status.reconnecting { background: #fef9c3; color: #854d0e; }
    .live-status.disconnected { background: #fee2e2; color: #991b1b; }
    .panel { border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px; margin-bottom: 12px; background: #fff; }
    .panel h3 { margin: 0 0 10px; font-size: 16px; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
    .panel h4 { margin: 14px 0 8px; font-size: 14px; color: #334155; }
    .stats { display: grid; gap: 8px; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); }
    .stats article { border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px; display: grid; gap: 4px; }
    .stats article.warn { border-color: #fca5a5; background: #fef2f2; }
    .stats strong { font-size: 11px; color: #64748b; }
    .attention { margin: 10px 0 0; color: #b45309; font-weight: 600; }
    .filters { margin-bottom: 10px; }
    .filter-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; margin-top: 8px; }
    .filter-grid label { display: grid; gap: 6px; }
    .filter-grid span { font-size: 12px; color: #475569; font-weight: 600; }
    .actions-row { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; margin: 10px 0; }
    .balanced { color: #15803d; font-weight: 600; }
    .unbalanced { color: #b91c1c; font-weight: 600; }
    .warn-cell { color: #b91c1c; font-weight: 600; }
    .empty-note { color: #64748b; margin: 8px 0 0; }
    .link-cell { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .link-cell a { color: #1d4ed8; font-weight: 600; text-decoration: none; font-size: 0.85rem; }
    .tech-link { border: 0; background: none; color: #64748b; font-size: 0.8rem; cursor: pointer; padding: 0; }
    .tech-link:hover { color: #1d4ed8; }
    .technical-panel { margin-top: 12px; border: 1px dashed #cbd5e1; border-radius: 8px; padding: 10px; background: #f8fafc; }
    .tech-grid { display: grid; gap: 8px; margin-bottom: 8px; }
    .copy-btn { border: 0; background: none; color: #475569; font-family: monospace; font-size: 0.8rem; cursor: pointer; text-align: left; padding: 0; word-break: break-all; }
    .copy-btn:hover { color: #1d4ed8; }
    .error { color: #b91c1c; margin-top: 8px; }
  `]
})
export class InventoryOperabilityComponent implements OnInit {
  private readonly costingApi = inject(InventoryCostingApiService);
  private readonly catalog = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusTranslationKey = liveStatusTranslationKey;

  readonly loading = signal(true);
  readonly outboxError = signal(false);
  readonly auditError = signal(false);
  readonly error = signal('');
  readonly reconciling = signal(false);
  readonly retryingMovementId = signal<string | null>(null);
  readonly outboxSummary = signal<InventoryGlPostingOutboxSummary | null>(null);
  readonly reconciliation = signal<InventoryReconciliationReport | null>(null);
  readonly auditItems = signal<InventoryAuditTrailEntry[]>([]);
  readonly auditTotalCount = signal(0);
  readonly expandedEntry = signal<InventoryAuditTrailEntry | null>(null);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);

  auditPageSize = 20;
  auditFirst = 0;
  private auditPage = 1;
  reconciliationFilters = { offeringId: '', warehouseId: '' };
  auditFilters = { fromDate: '', toDate: '', category: '' };

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.load(false)
    );
    this.catalog.listOfferings().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: rows => this.offerings.set(rows.filter(item => item.inventoryEnabled)),
      error: () => this.offerings.set([])
    });
    this.catalog.listWarehouses().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: rows => this.warehouses.set(rows),
      error: () => this.warehouses.set([])
    });
    this.load();
  }

  hasAccess(): boolean {
    const context = this.contextService.context();
    if (!context) return false;
    if (new Set(context.roles).has('SystemAdmin')) return true;
    const permissions = new Set(context.permissions.map(item => item.key));
    return permissions.has('inventory.costing.view') || permissions.has('operations.control-room.view');
  }

  load(showLoading = true): void {
    if (!this.hasAccess()) {
      this.loading.set(false);
      return;
    }
    if (showLoading) this.loading.set(true);
    this.error.set('');
    this.loadOutboxSummary();
    this.loadAuditTrail(showLoading);
    this.runReconciliation(false);
  }

  runReconciliation(showBusy = true): void {
    if (showBusy) this.reconciling.set(true);
    this.costingApi.runReconciliation({
      offeringId: this.reconciliationFilters.offeringId || undefined,
      warehouseId: this.reconciliationFilters.warehouseId || undefined
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: report => {
        this.reconciliation.set(report);
        this.reconciling.set(false);
      },
      error: err => {
        this.reconciling.set(false);
        if (err?.status === 403) {
          this.error.set(this.t('catalog.costing.states.noPermissionMessage'));
        } else {
          this.error.set(this.t('catalog.costing.operability.reconciliationFailed'));
        }
      }
    });
  }

  exportReconciliation(): void {
    this.costingApi.exportReconciliation({
      offeringId: this.reconciliationFilters.offeringId || undefined,
      warehouseId: this.reconciliationFilters.warehouseId || undefined
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: csv => this.downloadCsv(csv, 'inventory-reconciliation.csv'),
      error: () => this.error.set(this.t('catalog.costing.operability.reconciliationFailed'))
    });
  }

  searchAuditTrail(): void {
    this.auditFirst = 0;
    this.auditPage = 1;
    this.loadAuditTrail(true);
  }

  onAuditPage(event: TableLazyLoadEvent): void {
    this.auditPageSize = event.rows ?? this.auditPageSize;
    this.auditFirst = event.first ?? 0;
    this.auditPage = Math.floor(this.auditFirst / this.auditPageSize) + 1;
    this.loadAuditTrail();
  }

  retryGl(movementId: string): void {
    this.retryingMovementId.set(movementId);
    this.catalog.retryInventoryMovementGlPosting(movementId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.retryingMovementId.set(null);
        this.load(false);
      },
      error: () => {
        this.error.set(this.t('catalog.costing.gl.retryFailed'));
        this.retryingMovementId.set(null);
      }
    });
  }

  canRetryRow(row: InventoryAuditTrailEntry): boolean {
    if (!row.movementId) return false;
    const status = row.postingStatus || row.operationalStatus;
    return status === 'Failed' || status === 'Pending' || status === 'PendingRetry' || status === 'Blocked';
  }

  offeringOptions(): { label: string; value: string }[] {
    return this.offerings().map(item => ({ label: `${item.code} - ${item.title}`, value: item.id }));
  }

  warehouseOptions(): { label: string; value: string }[] {
    return this.warehouses().map(item => ({ label: `${item.code} - ${item.name}`, value: item.id }));
  }

  categoryOptions(): { label: string; value: string }[] {
    return ['movement', 'cost_ledger', 'gl_outbox', 'revaluation', 'landed_cost'].map(value => ({
      value,
      label: this.categoryLabel(value)
    }));
  }

  resolveOffering(offeringId: string): string {
    const offering = this.offerings().find(item => item.id === offeringId);
    return offering ? `${offering.code} - ${offering.title}` : '-';
  }

  resolveWarehouse(warehouseId: string): string {
    const warehouse = this.warehouses().find(item => item.id === warehouseId);
    return warehouse ? `${warehouse.code} - ${warehouse.name}` : '-';
  }

  categoryLabel(category: string): string {
    const key = `catalog.costing.operability.categories.${category}`;
    const translated = this.t(key);
    return translated === key ? category : translated;
  }

  driftReasonLabel(reason: string): string {
    const key = `catalog.costing.operability.driftReasons.${reason}`;
    const translated = this.t(key);
    return translated === key ? reason : translated;
  }

  copyText(value: string): void {
    void navigator.clipboard.writeText(value);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private loadOutboxSummary(): void {
    this.outboxError.set(false);
    this.costingApi.getGlPostingOutboxSummary().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: summary => this.outboxSummary.set(summary),
      error: err => {
        this.outboxSummary.set(null);
        this.outboxError.set(err?.status !== 403);
      }
    });
  }

  private loadAuditTrail(finishLoading = false): void {
    this.auditError.set(false);
    this.costingApi.getAuditTrail({
      category: this.auditFilters.category || undefined,
      fromUtc: this.toUtcStart(this.auditFilters.fromDate),
      toUtc: this.toUtcEnd(this.auditFilters.toDate),
      page: this.auditPage,
      pageSize: this.auditPageSize
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: page => {
        this.auditItems.set(page.items);
        this.auditTotalCount.set(page.totalCount);
        if (finishLoading) this.loading.set(false);
      },
      error: err => {
        this.auditItems.set([]);
        this.auditTotalCount.set(0);
        this.auditError.set(err?.status !== 403);
        if (finishLoading) this.loading.set(false);
      }
    });
  }

  private toUtcStart(value: string): string | undefined {
    const trimmed = (value ?? '').trim();
    if (!trimmed) return undefined;
    return new Date(`${trimmed}T00:00:00.000Z`).toISOString();
  }

  private toUtcEnd(value: string): string | undefined {
    const trimmed = (value ?? '').trim();
    if (!trimmed) return undefined;
    return new Date(`${trimmed}T23:59:59.999Z`).toISOString();
  }

  private downloadCsv(content: string, fileName: string): void {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}
